import React, { useRef, useCallback } from "react";
import Marquee from "react-fast-marquee";
import "../../scss/partners.scss";
import { useI18n } from "../../i18n/I18nProvider";
import type { TranslationKey } from "../../i18n/translations";

import KAS from "./img/Kas_winieta.png";
import CBZC from "./img/Logo_CBZC.png";
import PTI from "./img/PTI.png";
import PIIT from "./img/piit.png";

type PartnerSlide = {
  id: number;
  nameKey: TranslationKey;
  roleKey: TranslationKey;
  descKey: TranslationKey;
  image: string;
  link: string;
};

const PARTNERS: PartnerSlide[] = [
  {
    id: 1,
    nameKey: "partners_kas_name",
    roleKey: "partners_kas_role",
    image: KAS,
    link: "https://www.gov.pl/web/kas",
    descKey: "partners_kas_desc",
  },
  {
    id: 2,
    nameKey: "partners_cbzc_name",
    roleKey: "partners_cbzc_role",
    image: CBZC,
    link: "https://cbzc.policja.gov.pl/",
    descKey: "partners_cbzc_desc",
  },
  {
    id: 3,
    nameKey: "partners_pti_name",
    roleKey: "partners_pti_role",
    image: PTI,
    link: "https://pti.org.pl/",
    descKey: "partners_pti_desc",
  },
  /*
  {
    id: 4,
    nameKey: "partners_piit_name",
    roleKey: "partners_piit_role",
    image: PIIT,
    link: "https://piit.org.pl/",
    descKey: "partners_piit_desc",
  },
  */
];

const BASE_SPEED = 40;

const PartnersComponent: React.FC = () => {
  const { t } = useI18n();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const drag = useRef({
    active: false,
    pending: false,
    startX: 0,
    lastX: 0,
    inputType: "mouse",
    didDrag: false,
    anims: [] as { anim: Animation; t0: number; dur: number; w: number }[],
  });

  const gatherAnims = useCallback(() => {
    const w = wrapperRef.current;
    if (!w) return [];
    const result: typeof drag.current.anims = [];
    w.querySelectorAll(".rfm-marquee").forEach((el) => {
      el.getAnimations().forEach((a) => {
        const dur = (a.effect?.getComputedTiming().duration as number) ?? 0;
        if (dur > 0) {
          result.push({
            anim: a,
            t0: (a.currentTime as number) ?? 0,
            dur,
            w: (el as HTMLElement).offsetWidth,
          });
        }
      });
    });
    return result;
  }, []);

  const beginDrag = useCallback((clientX: number, inputType: "mouse" | "touch") => {
    drag.current = {
      active: false,
      pending: true,
      startX: clientX,
      lastX: clientX,
      inputType,
      didDrag: false,
      anims: [],
    };
  }, []);

  const moveDrag = useCallback((clientX: number) => {
    const d = drag.current;
    if (!d.pending && !d.active) return false;
    const dx = clientX - d.startX;
    const activationThreshold = d.inputType === "mouse" ? 5 : 4;

    if (d.pending && Math.abs(dx) > activationThreshold) {
      const anims = gatherAnims();
      if (!anims.length) {
        d.pending = false;
        return false;
      }
      anims.forEach(({ anim }) => anim.pause());
      d.anims = anims.map((item) => ({
        ...item,
        t0: (item.anim.currentTime as number) ?? item.t0,
      }));
      d.active = true;
      d.pending = false;
      d.didDrag = true;
      d.startX = clientX;
      d.lastX = clientX;
      return true;
    }

    if (d.active) {
      const deltaX = clientX - d.lastX;
      d.lastX = clientX;
      d.anims.forEach(({ anim, dur, w }) => {
        const currentTime = (anim.currentTime as number) ?? 0;
        const shift = (deltaX / w) * dur;
        anim.currentTime = ((currentTime - shift) % dur + dur) % dur;
      });
      return true;
    }

    return false;
  }, [gatherAnims]);

  const endDrag = useCallback(() => {
    const d = drag.current;
    if (d.active) {
      d.anims.forEach(({ anim }) => anim.play());
    }
    d.active = false;
    d.pending = false;
    d.anims = [];
    d.lastX = 0;
  }, []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    beginDrag(e.touches[0].clientX, "touch");
  }, [beginDrag]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!e.touches.length) return;
    if (moveDrag(e.touches[0].clientX)) {
      e.preventDefault();
    }
  }, [moveDrag]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    beginDrag(e.clientX, "mouse");
  }, [beginDrag]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (moveDrag(e.clientX)) {
      e.preventDefault();
    }
  }, [moveDrag]);

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (drag.current.didDrag) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.didDrag = false;
    }
  }, []);

  return (
    <section
      className="cyber-partners-section"
      aria-label={t("partners_section_label")}
    >
      <div className="partners__inner">
        <header className="partners__header">
          <h2 className="partners__title">
            {t("partners_title_prefix")}<span>{t("partners_title_highlight")}</span>
          </h2>
          <div className="partners__divider"></div>
          <p className="partners__subtitle">
            {t("partners_subtitle")}
          </p>
        </header>

        <div
          className="partners-marquee-wrapper"
          ref={wrapperRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={endDrag}
          onTouchCancel={endDrag}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onClickCapture={onClickCapture}
          onDragStart={(e) => e.preventDefault()}
        >
          <Marquee
            className="partners-marquee"
            pauseOnHover={true}
            gradient={false}
            speed={BASE_SPEED}
            direction="left"
            autoFill={true}
          >
            {PARTNERS.map((partner) => (
              <a
                key={partner.id}
                className="cyber-partner-tile"
                href={partner.link}
                target="_blank"
                rel="noopener noreferrer"
                draggable={false}
                aria-label={`${t("partners_link_label")} ${t(partner.nameKey)}`}
              >
                <div className="tile-glow"></div>

                <div className="tile-content-wrapper">
                  <div className="partner-logo-container">
                    <img src={partner.image} alt={t(partner.nameKey)} draggable={false} />
                  </div>

                  <div className="partner-text-container">
                    <h3 className="partner-name">{t(partner.nameKey)}</h3>
                    <p className="partner-role">{t(partner.roleKey)}</p>
                    <p className="partner-description">
                      {t(partner.descKey)}
                    </p>
                  </div>
                </div>
              </a>
            ))}
          </Marquee>
        </div>
      </div>
    </section>
  );
};

export default PartnersComponent;